// src/context/ProfileContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { httpService } from "../services/httpService";
import { TokenContext } from "./TokenContext";

const ProfileContext = createContext();
const defaultTime = "09:00";

// Menus used in sidebar/thememaker checks.
const KNOWN_MENU_NAMES = [
  "Dashboard",
  "Booking Management",
  "Listing Bookings",
  "Calendar",
  "Service Management",
  "Add-On Service",
  "Grooming Services",
  "Boarding Services",
  "Day Care Services",
  "Dog Walking Services",
  "Vet Clinic Services",
  "Clients/Pets",
  "Settings",
  "Profile Settings",
  "Online Profile",
  "Room Management",
  "Service Amenities",
  "Size Management",
  "Coat Management",
  "Store Management",
  "Inventory Management",
  "Inventory/Products",
  "Suppliers",
  "Purchase Bill",
  "Assigned Inventory",
  "Staff Management",
  "Order Management",
  "Enquiry",
  "Orders",
  "Payments Received",
  "POS Sale",
  "Invoice",
  "Expense Management",
  "Expenses",
  "Payments",
  "Reports",
  "Standard Reports",
];

// Frontend fallback: after expiry, keep only free-plan menus active.
const FREE_PLAN_ALLOWED_MENUS = new Set([
  "Dashboard",
  "Booking Management",
  "Listing Bookings", // Booking List
  "Calendar",
  "Service Management",
  "Add-On Service",
  "Grooming Services",
  "Boarding Services",
  "Dog Walking Services",
  "Clients/Pets",
  "Reports",
  "Standard Reports",
  "Settings",
  "Profile Settings",
  "Online Profile",
  "Room Management",
]);

const buildEffectiveMenuPermissions = (
  apiPermissions = [],
  forceFree = false,
) => {
  if (!forceFree) return Array.isArray(apiPermissions) ? apiPermissions : [];

  const byName = new Map(
    (Array.isArray(apiPermissions) ? apiPermissions : []).map((p) => [
      p?.menu_name,
      p,
    ]),
  );

  return KNOWN_MENU_NAMES.map((menuName) => {
    const existing = byName.get(menuName);
    const isAllowed = FREE_PLAN_ALLOWED_MENUS.has(menuName);

    if (existing) {
      return {
        ...existing,
        is_active: isAllowed,
      };
    }

    return {
      menu_name: menuName,
      is_active: isAllowed,
      role_permissions: [],
    };
  });
};

export const useProfile = () => useContext(ProfileContext);

// initial state helper (keeps clearProfile simple)
const getInitialState = () => ({
  profileData: null,
  businessHours: {},
  addresses: [],
  addressType: "Office",
  profileId: null,
  panNumber: "",
  vendortype: "",
  aadhaarUploaded: false,
  addressProofUploaded: false,
  bizProofUploaded: false,
  countMap: {},
  selectedFree: {},
  ownerName: "",
  ownerIdProofType: "",
  ownerIdNumber: "",
  partnerName: "",
  businessType: "",
  businessProofs: [],
  businessProofType: "",
  businessProofNumber: "",
  acceptedTerms: false,
  legalName: "",
  panUploaded: false,
  idUploaded: false,
  planname: "",
  services: [],
  rawServiceCategory: [],
  checkedService: [],
  profilePercent: undefined,
  businessAddressProofType: "",
  socialMediaLinks: {
    facebook: "",
    youtube: "",
    linkedin: "",
    instagram: "",
    website: "",
  },
  formData: {
    name: "",
    companyName: "",
    phone: "",
    email: "",
    about: "",
    whatsapp: "",
    brand: "",
    tagline: "",
    BusinessType: "",
  },
  // New menu permissions state
  menuPermissions: [],
  isPlanExpired: false,
  planExpiryDate: null,
  userLimit: 1,
});

export const ProfileProvider = ({ children }) => {
  // states (initialized from getInitialState)
  const initial = getInitialState();
  const [profileData, setProfileData] = useState(initial.profileData);
  const [businessHours, setBusinessHours] = useState({
    businessHours: {},
    specialBusinessHours: [],
  });
  const [isBusinessHour, setIsBusinessHour] = useState(true);
  const [addresses, setAddresses] = useState(initial.addresses);
  const [addressType, setAddressType] = useState(initial.addressType);
  const [profileId, setProfileId] = useState(initial.profileId);
  const [panNumber, setPanNumber] = useState(initial.panNumber);
  const [vendortype, setVendorType] = useState(initial.vendortype);
  const [aadhaarUploaded, setAadhaarUploaded] = useState(
    initial.aadhaarUploaded,
  );
  const [addressProofUploaded, setAddressProofUploaded] = useState(
    initial.addressProofUploaded,
  );
  const [bizProofUploaded, setBizProofUploaded] = useState(
    initial.bizProofUploaded,
  );
  const [isSuspended, setIsSuspended] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [businessProofs, setBusinessProofs] = useState(initial.businessProofs);
  const [countMap, setCountMap] = useState(initial.countMap);
  const [selectedFree, setSelectedFree] = useState(initial.selectedFree);

  const [ownerName, setOwnerName] = useState(initial.ownerName);
  const [ownerIdProofType, setOwnerIdProofType] = useState(
    initial.ownerIdProofType,
  );
  const [ownerIdNumber, setOwnerIdNumber] = useState(initial.ownerIdNumber);
  const [partnerName, setPartnerName] = useState(initial.partnerName);
  const [businessType, setBusinessType] = useState(initial.businessType);
  const [businessProofType, setBusinessProofType] = useState(
    initial.businessProofType,
  );
  const [businessProofNumber, setBusinessProofNumber] = useState(
    initial.businessProofNumber,
  );
  const [acceptedTerms, setAcceptedTerms] = useState(initial.acceptedTerms);
  const [legalName, setLegalName] = useState(initial.legalName);
  const [panUploaded, setPanUploaded] = useState(initial.panUploaded);
  const [idUploaded, setIdUploaded] = useState(initial.idUploaded);
  const [planname, setPlanname] = useState(initial.planname);
  const [services, setServices] = useState(initial.services);
  const [rawServiceCategory, setRawServiceCategory] = useState(
    initial.rawServiceCategory,
  );
  const [checkedService, setChecked] = useState(initial.checkedService);
  const [profilePercent, setCompletion] = useState(initial.profilePercent);
  const [businessAddressProofType, setBusinessAddressProofType] = useState(
    initial.businessAddressProofType,
  );

  const [socialMediaLinks, setSocialMediaLinks] = useState(
    initial.socialMediaLinks,
  );
  const [formData, setFormData] = useState(initial.formData);
  const [consentForm, setConsentForm] = useState(null);
  // New menu permissions states - simplified
  const [menuPermissions, setMenuPermissions] = useState(
    initial.menuPermissions,
  );
  const [isPlanExpired, setIsPlanExpired] = useState(initial.isPlanExpired);
  const [planExpiryDate, setPlanExpiryDate] = useState(initial.planExpiryDate);
  const [userLimit, setUserLimit] = useState(initial.userLimit);

  // loading / error
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState(null);

  const [roleData, setRoleData] = useState(null);
  const [roleMenuPermissions, setRoleMenuPermissions] = useState([]);

  // refs
  const isMounted = useRef(true);
  const isFetchingRef = useRef(false);

  // TokenContext to react to login/logout
  const { decodedData } = React.useContext(TokenContext);

  // Utility: Convert 24-hour time to 12-hour format
  const to12HourFormat = (time24) => {
    if (!time24) return to12HourFormat(defaultTime);
    const [hourStr, min] = time24.split(":");
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour.toString().padStart(2, "0")}:${min} ${ampm}`;
  };

  // Utility: Convert 12-hour time to 24-hour format
  const to24HourFormat = (time12) => {
    if (!time12) return defaultTime;
    const [time, modifier] = time12.split(" ");
    let [hours, minutes] = time.split(":");
    if (hours === "12") hours = modifier === "AM" ? "00" : "12";
    else if (modifier === "PM") hours = String(parseInt(hours, 10) + 12);
    return `${hours.padStart(2, "0")}:${minutes}`;
  };

  // Helper function to check if a menu is allowed by menu name
  const isMenuAllowed = (menuName) => {
    const permission = menuPermissions.find((p) => p.menu_name === menuName);
    return permission ? permission.is_active : false;
  };

  // Helper function to get menu by name
  const getMenuByName = (menuName) => {
    return menuPermissions.find((p) => p.menu_name === menuName);
  };

  // Get all actions for a given menu name (based on role permissions)
const getActionsForMenu = (menuName) => {
  const menu = roleMenuPermissions.find(
    (m) => m.menu_name?.trim() === menuName.trim(),
  );
  return menu?.actions || [];
};

const isActionEnabled = (menuName, actionName) => {
  const menu = roleMenuPermissions.find(
    (m) => m.menu_name?.trim() === menuName.trim(),
  );
  if (!menu) return false;
  const action = menu.actions.find(
    (a) => a.action_name?.trim() === actionName.trim(),
  );
  return action ? action.is_enabled : false;
};


const isMenuEnabled = (menuName) => {
  const menu = roleMenuPermissions.find(
    (m) => m.menu_name?.trim() === menuName.trim(),
  );
  return menu ? menu.is_enabled : false;
};

  // fetchProfile - returns fetched data or throws
  const fetchProfile = async () => {
    // avoid concurrent fetches
    if (isFetchingRef.current) return null;
    isFetchingRef.current = true;
    setLoadingProfile(true);
    setProfileError(null);

    try {
      const response = await httpService.get("vendors/getOnlineProfile");
      const data = response.data || {};
      const profile = data.profile || {};

      // vendor type & basic fields
      setVendorType(data?.vendor_type || "");
      setProfileData(data);
      const roleDataFromApi = data.role_data || null;
      setRoleData(roleDataFromApi);
      const roleMenus = roleDataFromApi?.menu_permissions || [];
      setRoleMenuPermissions(roleMenus);
      setProfileId(data._id || null);
      setConsentForm(data.consentForm || null);
      setIsBusinessHour(
        typeof data.isBusinessHour === "boolean" ? data.isBusinessHour : true,
      );

      // Suspended Account Check
      if (data?.isSuspended) {
        setIsSuspended(true);
        setShowLoginPopup(true);
        return null;
      }

      if (
        data?.staffSettings?.no_login_post_working_hours &&
        ["executive"].includes(decodedData?.data?.user?.role_data?.name)
      ) {
        const now = new Date();
        const currentDay = now.toLocaleString("en-US", { weekday: "long" });
        const currentMinutes = now.getHours() * 60 + now.getMinutes();

        const todaySchedule = (data.businessHours || []).find(
          (item) => item.day === currentDay,
        );

        let isAllowed = false;

        if (todaySchedule && !todaySchedule.closed) {
          isAllowed = (todaySchedule.slots || []).some((slot) => {
            const [startHour, startMin] = slot.start.split(":").map(Number);
            const [endHour, endMin] = slot.end.split(":").map(Number);

            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;

            return (
              currentMinutes >= startMinutes && currentMinutes <= endMinutes
            );
          });
        }


        if (!isAllowed) {
          setShowLoginPopup(true);
          return null;
        }
      }
      const deduplicateMenuPermissions = (permissions) => {
        const map = new Map();

        for (const perm of permissions) {
          const name = perm.menu_name;
          if (!name) continue;

          const existing = map.get(name);
          if (!existing) {
            map.set(name, perm);
          } else {
            // Keep the one with MORE true flags
            const existingScore =
              (existing.admin ? 1 : 0) +
              (existing.manager ? 1 : 0) +
              (existing.executive ? 1 : 0);
            const newScore =
              (perm.admin ? 1 : 0) +
              (perm.manager ? 1 : 0) +
              (perm.executive ? 1 : 0);
            if (newScore > existingScore) {
              map.set(name, perm);
            }
          }
        }

        return Array.from(map.values());
      };

      const apiMenuPermissions = Array.isArray(data.menu_permissions)
        ? deduplicateMenuPermissions(data.menu_permissions)
        : [];

      const accountType = data?.accountType;
      const normalizePlanKey = (value) =>
        String(value || "")
          .trim()
          .toLowerCase();
      const matchesPlan = (plan, targetName) => {
        const target = normalizePlanKey(targetName);
        if (!target) return false;
        const keys = [
          plan?.plan_name,
          plan?.plan_tag,
          plan?.planname,
          plan?.name,
        ].map(normalizePlanKey);
        return keys.some(
          (key) =>
            key &&
            (key === target || key.includes(target) || target.includes(key)),
        );
      };
      const apiServiceCategories = Array.isArray(data?.service_category)
        ? data.service_category
        : [];
      const subscriptionPlans = Array.isArray(data?.subscriptionPlan)
        ? data.subscriptionPlan
        : [];

      let shouldForceFreeMenuPermissions = false;
      let resolvedPlan = null;
      if (subscriptionPlans.length > 0) {
        const now = new Date();
        const planFromAccountType = subscriptionPlans.find((plan) =>
          matchesPlan(plan, accountType),
        );
        const activePlan = subscriptionPlans.find((plan) => plan.status == 1 || plan.status == "active");

        const latestPlan = [...subscriptionPlans].sort(
          (a, b) => new Date(b.end_date) - new Date(a.end_date),
        )[0];
        resolvedPlan = activePlan || latestPlan || null;
        const latestEndDate = latestPlan?.end_date
          ? new Date(latestPlan.end_date)
          : null;

        const isExpired = activePlan?.is_expired || false; //!activePlan && !!latestEndDate && latestEndDate < now;
        resolvedPlan =
          activePlan ||
          planFromAccountType ||
          (!isExpired ? latestPlan : null) ||
          null;
        setIsPlanExpired(isExpired);
        setPlanExpiryDate(latestEndDate ? latestEndDate.toISOString() : null);
        setUserLimit(Number(activePlan?.user_limit));
        // shouldForceFreeMenuPermissions = isExpired;
      } else {
        resolvedPlan = null;
        setIsPlanExpired(false);
        setPlanExpiryDate(null);
        setUserLimit(1);
      }

      setPlanname(
        accountType ||
          resolvedPlan?.plan_name ||
          resolvedPlan?.planname ||
          resolvedPlan?.name ||
          data?.vendor_type ||
          "",
      );

      setMenuPermissions(
        buildEffectiveMenuPermissions(
          apiMenuPermissions,
          shouldForceFreeMenuPermissions,
        ),
      );

      // build services
      const effectiveServiceCategories =
        apiServiceCategories.length > 0
          ? apiServiceCategories
          : Array.isArray(resolvedPlan?.plan_category)
            ? resolvedPlan.plan_category
            : [];

      const serviceObjects = effectiveServiceCategories.flatMap((category) => {
        // If the category has service_type items, use them
        if (
          Array.isArray(category.service_type) &&
          category.service_type.length > 0
        ) {
          return category.service_type.map((type) => ({
            serviceCategoryTypeId: type.service_category_type_id,
            service_category_type_id: type.service_category_type_id,
            id: category.service_category_id,
            service_category_id: category.service_category_id,
            service_type_id: type.service_type_id,
            type_id: category.type_id,
            name: type.service_type_name,
            description: type.description,
            photo: type.photo,
            category_name: category.service_category_name,
            isActive: type.is_active,
            bookable_online: type.bookable_online,
          }));
        }

        // Fallback: treat the category itself as a service type
        // Use the category's own details (since no service_type exists)
        return {
          serviceCategoryTypeId: category.service_category_id,
          service_category_type_id: category.service_category_id,
          id: category.service_category_id,
          service_category_id: category.service_category_id,
          service_type_id: category.type_id, // or use a generated ID
          type_id: category.type_id,
          name: category.service_category_name,
          description: category.description || "",
          photo: category.photo || null,
          category_name: category.service_category_name,
          isActive: category.isActive === true ? 1 : 0,
          bookable_online: 1, 
        };
      });
      setRawServiceCategory(effectiveServiceCategories);
      setServices(serviceObjects);

      // social links
      setSocialMediaLinks({
        facebook: data.socialMedia?.facebook || "",
        youtube: data.socialMedia?.youtube || "",
        linkedin: data.socialMedia?.linkedin || "",
        instagram: data.socialMedia?.instagram || "",
        website: data.socialMedia?.website || "",
      });

      // form data
      setFormData({
        name: profile.name || "",
        companyName: profile.companyName || "",
        phone: profile.phone || "",
        email: profile.email || "",
        about: profile.about || "",
        whatsapp: profile.whatsapp || "",
        brand: profile.brand || "",
        tagline: profile.tagline || data.tagline || "",
        BusinessType: profile.BusinessType || "",
      });

      // count map (guard)
      const map = {};
      (data.services || []).forEach((service) => {
        const key = (service.category || "").trim().toLowerCase() || "unknown";
        map[key] = service.count || service.services?.length || 0;
      });
      setCountMap(map);

      const formattedRegular = {};
      (data.businessHours || []).forEach(
        ({ day, slots = [], closed = false, allDay = false }) => {
          formattedRegular[day] = {
            slots: slots.length > 0 ? slots : [{ start: "", end: "" }],
            closed,
            allDay,
          };
        },
      );

      // Set both regular and special business hours together
      setBusinessHours({
        businessHours: formattedRegular,
        specialBusinessHours: data.specialBusinessHours || [],
      });
      setConsentForm(data.consentForm || null);

      setSelectedFree(data.selectedservices || {});
      setChecked(data.services || []);

      const office = data.address?.officeAddress || {};

      const fetchedAddresses = [
        {
          type: "Office",
          locality: office.locality || "",
          state: office.state || "",
          pincode: office.pincode || "",
          address: office.full_address || "",
          city: office.city || "",
          locality: office.locality || "",
          landmark: office.landmark || "",
          full_address: office.full_address || "",
          floor: office.floor || "",
          buildingName: office.buildingName || "",
        },
      ];
      setAddresses(fetchedAddresses);

      const kyc = data.kyc || {};
      setPanNumber(kyc.panNumber || "");
      setOwnerName(kyc.ownerName || "");
      setOwnerIdProofType(kyc.ownerIdProofType || "");
      setOwnerIdNumber(kyc.ownerIdNumber || "");
      setPartnerName(kyc.partnerName || "");
      setBusinessType(kyc.businessType || "");
      setBusinessProofType(kyc.businessProofType || "");
      setBusinessProofNumber(kyc.businessProofNumber || "");
      setAcceptedTerms(kyc.acceptedTerms || false);
      setLegalName(kyc.legalName || "");
      setPanUploaded(!!kyc.panFile);
      setIdUploaded(!!kyc.ownerIdFile);
      setAadhaarUploaded(!!kyc.ownerIdFile);
      setAddressProofUploaded(!!kyc.businessAddressProofFile);
      setBizProofUploaded(!!kyc.businessProofFile);
      if (kyc.businessProofs && Array.isArray(kyc.businessProofs)) {
        // New array format
        setBusinessProofs(kyc.businessProofs);
        // Clear single fields for consistency
        setBusinessProofType("");
        setBusinessProofNumber("");
        setBizProofUploaded(kyc.businessProofs.length > 0);
      } else if (kyc.businessProofFile) {
        // Legacy single format - convert to array for frontend
        const legacyProof = {
          proofType: kyc.businessProofType || "Business Proof",
          proofNumber: kyc.businessProofNumber || "",
          proofFile: kyc.businessProofFile,
          uploadedAt: new Date().toISOString(),
          _id: `legacy_${Date.now()}`, // Generate temporary ID
        };
        setBusinessProofs([legacyProof]);
        setBusinessProofType(kyc.businessProofType || "");
        setBusinessProofNumber(kyc.businessProofNumber || "");
        setBizProofUploaded(true);
      } else {
        // No business proofs
        setBusinessProofs([]);
        setBizProofUploaded(false);
      }

      if (kyc.panDocumentUrl) setPanUploaded(true);
      if (kyc.ownerIdDocumentUrl) setIdUploaded(true);

      // profile completion
      if (data) {
        const completion = calculateProfileCompletion(data);
        setCompletion(completion.overall);
      }

      return data;
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      setProfileError(error);
      return null;
    } finally {
      if (isMounted.current) {
        setLoadingProfile(false);
        isFetchingRef.current = false;
      } else {
        isFetchingRef.current = false;
      }
    }
  };

  // clearProfile - reset state to initial values
  const clearProfile = () => {
    const init = getInitialState();
    setProfileData(init.profileData);
    setBusinessHours({ businessHours: {}, specialBusinessHours: [] });
    setIsBusinessHour(true);
    setAddresses(init.addresses);
    setAddressType(init.addressType);
    setProfileId(init.profileId);
    setPanNumber(init.panNumber);
    setVendorType(init.vendortype);
    setAadhaarUploaded(init.aadhaarUploaded);
    setAddressProofUploaded(init.addressProofUploaded);
    setBizProofUploaded(init.bizProofUploaded);
    setCountMap(init.countMap);
    setSelectedFree(init.selectedFree);

    setOwnerName(init.ownerName);
    setOwnerIdProofType(init.ownerIdProofType);
    setOwnerIdNumber(init.ownerIdNumber);
    setPartnerName(init.partnerName);
    setBusinessType(init.businessType);
    setBusinessProofType(init.businessProofType);
    setBusinessProofNumber(init.businessProofNumber);
    setAcceptedTerms(init.acceptedTerms);
    setLegalName(init.legalName);
    setPanUploaded(init.panUploaded);
    setIdUploaded(init.idUploaded);
    setPlanname(init.planname);
    setServices(init.services);
    setChecked(init.checkedService);
    setCompletion(init.profilePercent);
    setBusinessAddressProofType(init.businessAddressProofType);
    setBusinessProofs(init.businessProofs);
    setSocialMediaLinks(init.socialMediaLinks);
    setFormData(init.formData);

    // Clear menu permissions
    setMenuPermissions(init.menuPermissions);
    setIsPlanExpired(init.isPlanExpired);
    setPlanExpiryDate(init.planExpiryDate);
    setUserLimit(init.userLimit);

    setLoadingProfile(false);
    setProfileError(null);
  };

  // calculateProfileCompletion - unchanged logic except defensive checks
  const calculateProfileCompletion = (data) => {
    const percent = (filled, total) =>
      total === 0 ? 0 : Math.round((filled / total) * 100);

    const missingFields = {
      profile: [],
      address: [],
      bizHours: [],
      photos: [],
      social: [],
      services: [],
      kyc: [],
      video: [],
    };

    const checkField = (key, value, section) => {
      if (!value) missingFields[section].push(key);
      return !!value;
    };

    const profileFilled = [
      checkField("Name", data.profile?.name, "profile"),
      checkField("Company Name", data.profile?.companyName, "profile"),
      checkField("Phone", data.profile?.phone, "profile"),
      checkField("Email", data.profile?.email, "profile"),
      checkField("About", data.profile?.about, "profile"),
      checkField("Whatsapp", data.profile?.whatsapp, "profile"),
      checkField("Brand", data.profile?.brand, "profile"),
    ].filter(Boolean).length;

    const addressFilled = [
      checkField(
        "Office Full Address",
        data.address?.officeAddress?.full_address,
        "address",
      ),
    ].filter(Boolean).length;

    const photosFilled = [
      checkField("Profile Photo", data.photo?.profilePhoto, "photos"),
      checkField("Cover Photo", data.photo?.coverPhoto, "photos"),
      checkField(
        "Shop Photo",
        Array.isArray(data.photo?.shop) && data.photo.shop.length > 0,
        "photos",
      ),
      checkField(
        "Service Area Photo",
        Array.isArray(data.photo?.serviceArea) &&
          data.photo.serviceArea.length > 0,
        "photos",
      ),
    ].filter(Boolean).length;

    const socialFilled = [
      checkField("Facebook", data.socialMedia?.facebook, "social"),
      checkField("YouTube", data.socialMedia?.youtube, "social"),
      checkField("LinkedIn", data.socialMedia?.linkedin, "social"),
      checkField("Instagram", data.socialMedia?.instagram, "social"),
      checkField("Website", data.socialMedia?.website, "social"),
    ].filter(Boolean).length;

    const servicesFilled = data.services?.length || 0;

    const kycFilled = [
      checkField("Owner Name", data.kyc?.ownerName, "kyc"),
      checkField("PAN Number", data.kyc?.panNumber, "kyc"),
      checkField("PAN File", data.kyc?.panFile, "kyc"),
      checkField("Owner ID Proof Type", data.kyc?.ownerIdProofType, "kyc"),
      checkField("Owner ID Number", data.kyc?.ownerIdNumber, "kyc"),
      checkField("Owner ID File", data.kyc?.ownerIdFile, "kyc"),
      checkField("Business Type", data.kyc?.businessType, "kyc"),
      // checkField("Business Proof Type", data.kyc?.businessProofType, "kyc"),
      // checkField("Business Proof Number", data.kyc?.businessProofNumber, "kyc"),
      // checkField("Business Proof File", data.kyc?.businessProofFile, "kyc"),

      checkField(
        "Business Proofs",
        (data.kyc?.businessProofs && data.kyc.businessProofs.length > 0) ||
          data.kyc?.businessProofFile,
        "kyc",
      ),
      checkField(
        "Business Address Proof File",
        data.kyc?.businessAddressProofFile,
        "kyc",
      ),
      data.kyc?.businessType === "Partnership"
        ? checkField("Partner Name", data.kyc?.partnerName, "kyc")
        : true,
    ].filter(Boolean).length;

    const videoFilled = [
      ...(data.video?.shopVideo || []).map((file, i) =>
        checkField(`Shop Video ${i + 1}`, file, "video"),
      ),
    ].filter(Boolean).length;

    const percentages = {
      profile: percent(profileFilled, 7),
      address: percent(addressFilled, 2),
      bizHours: percent(
        Array.isArray(data.businessHours) ? data.businessHours.length : 0,
        7,
      ),
      photos: percent(photosFilled, 4),
      social: percent(socialFilled, 5),
      services: percent(servicesFilled, 1),
      kyc: percent(kycFilled, 11),
      video: percent(videoFilled, 1),
    };
    const sectionKeys = Object.keys(percentages);
    const total = sectionKeys.reduce((sum, key) => sum + percentages[key], 0);
    const overall = Math.round(total / sectionKeys.length);

    return { ...percentages, overall };
  };

  // toggleService unchanged
  const toggleService = (serviceCategoryTypeId) => {
    setServices((prevServices) =>
      prevServices.map((service) =>
        service.serviceCategoryTypeId === serviceCategoryTypeId
          ? { ...service, isActive: service.isActive === 1 ? 0 : 1 }
          : service,
      ),
    );
    setRawServiceCategory((prev) =>
      prev.map((category) => ({
        ...category,
        service_type: category.service_type.map((st) =>
          st.service_category_type_id === serviceCategoryTypeId
            ? { ...st, is_active: st.is_active === 1 ? 0 : 1 }
            : st,
        ),
      })),
    );
  };

  // update completion when profileData changes
  useEffect(() => {
    if (profileData) {
      const completion = calculateProfileCompletion(profileData);
      setCompletion(completion.overall);
    }
  }, [profileData]);

  // cleanup mount ref
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // auto-fetch when token/decodedData appears, auto-clear when removed
  useEffect(() => {
    if (decodedData && decodedData.data) {
      if (!isFetchingRef.current) {
        fetchProfile().catch(() => {});
      }
    } else {
      clearProfile();
    }
  }, [decodedData]);

      const handleLoginPopupOk = () => {
        setShowLoginPopup(false); // hide popup
        setIsSuspended(false);
        localStorage.removeItem("us_st_d"); // clear token/session
        clearProfile(); // reset profile context
        window.location.href = "/login"; // force logout
      };

  return (
    <ProfileContext.Provider
      value={{
        profileData,
        profileId,
        vendorId: profileId,
        formData,
        setFormData,
        fetchProfile,
        businessHours,
        isBusinessHour,
        setIsBusinessHour,
        socialMediaLinks,
        setSocialMediaLinks,
        setBusinessHours,
        setAddressType,
        addressType,
        addresses,
        vendortype,
        panNumber,
        setPanNumber,
        panUploaded,
        setPanUploaded,
        idUploaded,
        setIdUploaded,
        aadhaarUploaded,
        setAadhaarUploaded,
        addressProofUploaded,
        setAddressProofUploaded,
        bizProofUploaded,
        setBizProofUploaded,
        planname,
        profilePercent,
        setPlanname,
        ownerName,
        setOwnerName,
        ownerIdProofType,
        setOwnerIdProofType,
        countMap,
        selectedFree,
        setSelectedFree,
        setCountMap,
        checkedService,
        setChecked,
        ownerIdNumber,
        setOwnerIdNumber,
        partnerName,
        setPartnerName,
        businessType,
        setBusinessType,
        addressProofUploaded,
        businessProofs,
        setBusinessProofs,
        businessProofType,
        setBusinessProofType,
        services,
        setServices,
        businessAddressProofType,
        setBusinessAddressProofType,
        businessProofNumber,
        setBusinessProofNumber,
        acceptedTerms,
        setAcceptedTerms,
        legalName,
        setLegalName,
        panUploaded,
        setPanUploaded,
        idUploaded,
        setIdUploaded,
        toggleService,
        // existing additions
        clearProfile,
        loadingProfile,
        profileError,
        //for using in the service category selector
        rawServiceCategory,
        setRawServiceCategory,
        // New menu permission additions - simplified
        menuPermissions,
        setMenuPermissions,
        isMenuAllowed,
        getMenuByName,
        profileData,
        consentForm,
        setConsentForm,
        isPlanExpired,
        planExpiryDate,
        userLimit,
        roleData,
        roleMenuPermissions,
        getActionsForMenu,
        isActionEnabled,
        isMenuEnabled,
      }}
    >
      {children}
      {showLoginPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "8px",
              maxWidth: "400px",
              textAlign: "center",
            }}
          >
            <h2 style={{ color: "#dc2626" }}>Login Blocked</h2>

            <p style={{ marginTop: "1rem", lineHeight: "1.6" }}>
              {isSuspended ? (
                <>
                  Login is not allowed.
                  <br />
                  Your account is suspended.
                  <br />
                  Please contact support.
                </>
              ) : (
                "Login is not allowed outside working hours."
              )}
            </p>

            <button
              onClick={handleLoginPopupOk}
              style={{
                padding: "0.6rem 1.2rem",
                marginTop: "1.5rem",
                backgroundColor: "#dc2626", // 🔴 Red
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </ProfileContext.Provider>
  );
};
